import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getLangPrefix } from "../lib/langRoutes";

/**
 * Keeps `<title>` and key meta tags in sync with the current i18n language.
 * Crawlers (LinkedIn / WhatsApp / Telegram / Twitter) read the static HTML,
 * so the default OG tags in `index.html` are what they'll see. This hook
 * updates the browser-tab title and `og:locale` for users who switch language
 * while browsing.
 */
function setOrCreateMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const parts = selector.match(/\[([^=]+)="?([^"\]]+)"?\]/);
    if (parts) el.setAttribute(parts[1], parts[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const LOCALE_MAP = { it: "it_IT", en: "en_US", de: "de_DE", fr: "fr_FR", es: "es_ES", pl: "pl_PL" };

const SITE_ORIGIN = "https://machinepilot.eu";

function setOrCreateLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function useDocumentMeta() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  useEffect(() => {
    const title = t("seo.title", { defaultValue: "MachinePilot · AI decision layer per officine CNC" });
    const desc = t("seo.description", { defaultValue: "AI decision layer for CNC workshops." });
    const ogTitle = t("seo.ogTitle", { defaultValue: title });
    const ogDesc = t("seo.ogDescription", { defaultValue: desc });
    const lang = (i18n.language || "it").split("-")[0];
    const prefix = getLangPrefix(pathname);

    document.title = title;
    document.documentElement.setAttribute("lang", lang);
    setOrCreateMeta('meta[name="description"]', "content", desc);
    setOrCreateMeta('meta[property="og:title"]', "content", ogTitle);
    setOrCreateMeta('meta[property="og:description"]', "content", ogDesc);
    setOrCreateMeta('meta[property="og:locale"]', "content", LOCALE_MAP[lang] || "it_IT");
    setOrCreateMeta('meta[name="twitter:title"]', "content", ogTitle);
    setOrCreateMeta('meta[name="twitter:description"]', "content", ogDesc);
    // Canonical: italiano alla root (senza /it/), altre lingue /{lang}/;
    // hreflang reciproco con x-default sulla root italiana.
    const isIt = !prefix || prefix === "/it";
    const canonicalUrl = isIt ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${prefix}/`;
    setOrCreateMeta('meta[property="og:url"]', "content", canonicalUrl);
    setOrCreateMeta('meta[property="og:image"]', "content", `${SITE_ORIGIN}/og-cover.jpg`);
    setOrCreateMeta('meta[name="twitter:image"]', "content", `${SITE_ORIGIN}/og-cover.jpg`);
    setOrCreateLink("canonical", canonicalUrl);
    Object.keys(LOCALE_MAP).forEach((lng) =>
      setOrCreateLink("alternate", lng === "it" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}/${lng}/`, lng)
    );
    setOrCreateLink("alternate", `${SITE_ORIGIN}/`, "x-default");
  }, [t, i18n.language, pathname]);
}
